import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-dracula";
import {
  AppSettingsTabProps,
  ConditionOperator,
  TConfigItemType,
  TOnboardingConfig,
  TVerificationAppElement,
  VerificationAppDocumentType,
} from "../../models";
import { collectAjvErrorDetails } from "../AppSettingsSchema/ajvErrorFormatting";
import { useAppSettingsSchemaContext } from "../AppSettingsSchema/context";
import { validateLogic } from "../AppSettingsSchema/logicValidation";
import { SchemaDocumentation } from "../AppSettingsSchema/SchemaDocumentation";

const defaultCustomNotRequiredField: TVerificationAppElement = {
  type: VerificationAppDocumentType.MokBlock,
  fields: [
    {
      name: "field_name",
      label: "Field label",
      subtitle: "",
      type: TConfigItemType.TEXT,
      required: false,
    },
  ],
};

const defaultCustomFieldWithRules: TVerificationAppElement = {
  type: VerificationAppDocumentType.MokBlock,
  fields: [
    {
      name: "field_name",
      label: "Field label",
      subtitle: "",
      type: TConfigItemType.TEXT,
      required: true,
      visible: [
        {
          field: "field_name_to_check",
          op: ConditionOperator.IN,
          value: ["value", "secondValue"],
        },
      ],
    },
  ],
};

const defaultConfigRules = {
  bannedCountries: [
    "Afghanistan",
    "Burkina Faso",
    "Cambodia",
    "Cameroon",
    "Canada",
    "Central African Republic",
    "Congo",
    "Haiti",
    "Iran",
    "Iraq",
    "Jamaica",
    "Jordan",
    "Democratic People’s Republic of Korea",
    "Mali",
    "Mozambique",
    "Myanmar (Birma)",
    "Nigeria",
    "Somalia",
    "South Sudan",
    "Sudan",
    "Syrian Arab Republic",
    "Trinidad and Tobago",
    "Uganda",
    "United States of America",
    "Vanuatu",
    "Venezuela",
    "Vietnam",
    "Yemen",
    "Zimbabwe",
  ],
  showAcceptanceScreen: false,
};

export const AdvancedTab = memo(
  ({ config, onConfigChange }: AppSettingsTabProps) => {
    const { fullConfigValidator: validator, schema } = useAppSettingsSchemaContext();
    const editorRef = useRef<any>(null);
    const skipConfigSyncRef = useRef(false);
    const [editorValue, setEditorValue] = useState(() =>
      JSON.stringify(config || {}, null, 2),
    );
    const [showDocs, setShowDocs] = useState(false);
    const [validationError, setValidationError] = useState<{
      type: "json" | "schema";
      message: string;
      details?: string[];
    } | null>(null);

    useEffect(() => {
      if (skipConfigSyncRef.current) {
        skipConfigSyncRef.current = false;
        return;
      }
      setEditorValue(JSON.stringify(config || {}, null, 2));
    }, [config]);

    const handleEditorChange = useCallback(
      (newValue: string) => {
        setEditorValue(newValue);
        try {
          const parsed = JSON.parse(newValue) as TOnboardingConfig;
          skipConfigSyncRef.current = true;
          onConfigChange(() => parsed);
        } catch {
          // keep invalid json editable, but do not push into shared config
        }
      },
      [onConfigChange],
    );

    useEffect(() => {
      let parsed: TOnboardingConfig;
      try {
        parsed = JSON.parse(editorValue || "{}") as TOnboardingConfig;
      } catch (e: any) {
        setValidationError({
          type: "json",
          message: `Invalid JSON: ${e.message}`,
        });
        return;
      }

      const valid = validator(parsed);
      const schemaErrorCount = validator.errors?.length || 0;
      const details = !valid
        ? collectAjvErrorDetails(validator.errors)
        : [];

      if (schemaErrorCount < 10) {
        const logicErrors = validateLogic(parsed);
        if (logicErrors.length > 0) {
          if (details.length > 0) {
            details.push("");
          }
          details.push("Logic Validation:");
          logicErrors.forEach((err) => {
            details.push(`${err.path}:`);
            details.push(`  - ${err.message}`);
          });
        }
      }

      if (details.length > 0) {
        setValidationError({
          type: "schema",
          message:
            schemaErrorCount > 0
              ? "Validation Errors"
              : "Logic Validation Errors",
          details,
        });
      } else {
        setValidationError(null);
      }
    }, [editorValue, validator]);

    const addDefaultCustomField = useCallback(
      (type: "rules" | "element" = "element", withRules?: boolean) => {
        onConfigChange((prev) => {
          const next = { ...prev };
          if (type === "rules") {
            next.rules = defaultConfigRules;
          } else {
            next.elements = [...(prev.elements || [])];
            next.elements.push(
              withRules ? defaultCustomFieldWithRules : defaultCustomNotRequiredField,
            );
          }

          setTimeout(() => {
            if (editorRef.current) {
              const editor = editorRef.current.editor;
              const lines = JSON.stringify(next, null, 2).split("\n");
              let targetLine = 0;
              for (let i = 0; i < lines.length; i++) {
                if (
                  lines[i].includes(`"type": ${VerificationAppDocumentType.MokBlock}`) ||
                  lines[i].includes(
                    withRules
                      ? defaultCustomFieldWithRules.type
                      : defaultCustomNotRequiredField.type,
                  )
                ) {
                  targetLine = i + 1;
                  break;
                }
              }

              if (targetLine > 0) {
                editor.gotoLine(targetLine, 0, true);
                editor.focus();
              }
            }
          }, 100);
          return next;
        });
      },
      [onConfigChange],
    );

    return (
      <Paper
        sx={{
          margin: "12px 0",
          padding: "20px 32px",
          background: "#ffffff",
          borderRadius: "14px",
          border: "none",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Stack direction='row' spacing={2}>
                <Button onClick={() => addDefaultCustomField()}>Add field</Button>
                <Button onClick={() => addDefaultCustomField("element", true)}>
                  Add field with rules
                </Button>
                <Button onClick={() => addDefaultCustomField("rules")}>
                  Add default config rules
                </Button>
              </Stack>
              <Tooltip title='Toggle Schema Documentation'>
                <IconButton
                  onClick={() => setShowDocs(!showDocs)}
                  color={showDocs ? "primary" : "default"}
                  aria-label='Toggle Schema Documentation'
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={showDocs ? 7 : 12}>
                <Box sx={{ position: "relative" }}>
                  <AceEditor
                    ref={editorRef}
                    mode='json'
                    theme='dracula'
                    name='onboardingConfigEditor'
                    onChange={handleEditorChange}
                    value={editorValue}
                    width='100%'
                    height='600px'
                    fontSize={14}
                    setOptions={{
                      showLineNumbers: true,
                      tabSize: 2,
                      useWorker: false,
                    }}
                  />
                  {validationError && (
                    <Paper
                      elevation={3}
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 32,
                        padding: 2,
                        backgroundColor: "#fdeded",
                        color: "#5f2120",
                        maxWidth: "400px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        zIndex: 10,
                        border: "1px solid #f5c6cb",
                      }}
                    >
                      <Typography variant='subtitle2' fontWeight='bold'>
                        {validationError.message}
                      </Typography>
                      {validationError?.details?.map((msg, i) => (
                        <Typography
                          key={i}
                          variant='body2'
                          sx={{ wordBreak: "break-word" }}
                        >
                          {msg}
                        </Typography>
                      ))}
                    </Paper>
                  )}
                </Box>
              </Grid>
              {showDocs && (
                <Grid item xs={5}>
                  <SchemaDocumentation schema={schema} />
                </Grid>
              )}
            </Grid>
          </Box>
        </Stack>
      </Paper>
    );
  },
);
